import { getCountByStatus, useGetAllAuctions } from "@/api/auction/auction";
import NewAuctionDialog from "@/components/auction/NewAuctionDialog";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DataTable } from "@/components/ui/datatable";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import {
  publicAuctionAdvancedSearchConfig,
  publicAuctionColumns,
} from "@/config/dashboard/public-auction";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { Auction } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "auction-table-preferences";

const flatVisibleAuctionColumns = publicAuctionColumns.flatMap(
  (column: ColumnFormatted) => (column.visible ? column.key : []),
);

const AuctionDashboard = () => {
  const { t } = useTranslation();
  const {
    countByStatus,
    fetchParams,
    filterStatus,
    setFilterStatus,
    pagination,
    setPagination,
    currentCount,
  } = useDashboardParams({
    resourceName: "auction",
    resourceCounter: getCountByStatus,
    statusField: "ventesPublicationStatus",
  });

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { order } = JSON.parse(saved);
      return order || flatVisibleAuctionColumns;
    }
    return flatVisibleAuctionColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { visibility } = JSON.parse(saved);
        return (
          visibility || {
            ...publicAuctionColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...publicAuctionColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  const { data } = useGetAllAuctions(fetchParams);

  const navigate = useNavigate();

  const columns: ColumnDef<Auction, any>[] = useMemo(() => {
    return generateTable(publicAuctionColumns, "auction");
  }, []);

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">{t("dashboardTitleAuctions")}</p>
        <GlobalSearch
          config={publicAuctionAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          onValueChange={setFilterStatus}
          countByStatus={countByStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={publicAuctionAdvancedSearchConfig}
            resource="auction"
          />
          <AdvancedSort config={publicAuctionAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createAauction")}
            Dialog={NewAuctionDialog}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={(row) => navigate(`/edit/auction/${row.original.id}`)}
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
          columns={columns}
          data={data ?? []}
          storageKey={STORAGE_KEY}
          options={{
            enableColumnResizing: true,
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
          }}
        />
      </div>
    </div>
  );
};

export default AuctionDashboard;
